import type { ButtonHTMLAttributes } from 'react';
import { clsx } from '../clsx';
import { useStableId } from '../hooks';
import styles from './Toggle.module.css';

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible name; shown next to the switch unless `labelHidden`. */
  label: string;
  labelHidden?: boolean | undefined;
  /** Organisation default: shown at opacity .45, not operable, announced as read-only. */
  locked?: boolean | undefined;
  /** `default` 32×18 (Shell 15 notification matrix); `large` 34×20 (Einstellungen availability); `xlarge` 38×22 (Admin 05 module cards). */
  size?: 'default' | 'large' | 'xlarge' | undefined;
}

/**
 * On/off switch of the screens (`role="switch"`): accent when on, sand when off, white knob with
 * `--shadow-1`. The visible label names the switch (`aria-labelledby`); a hidden label becomes `aria-label`.
 */
export function Toggle({
  checked,
  onChange,
  label,
  labelHidden = false,
  locked = false,
  size = 'default',
  disabled,
  className,
  onClick,
  onKeyDown,
  id,
  ...rest
}: ToggleProps) {
  const inert = disabled || locked;
  const buttonId = useStableId(id);
  const labelId = `${buttonId}-label`;
  return (
    <span className={clsx(styles.wrap, className)}>
      <button
        id={buttonId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={labelHidden ? label : undefined}
        aria-labelledby={labelHidden ? undefined : labelId}
        aria-readonly={locked || undefined}
        aria-disabled={locked || undefined}
        disabled={disabled}
        className={clsx(
          styles.toggle,
          size === 'large' && styles.large,
          size === 'xlarge' && styles.xlarge,
          checked && styles.on,
          locked && styles.locked,
        )}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented || inert) return;
          onChange(!checked);
        }}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <span className={styles.knob} aria-hidden="true" />
      </button>
      {labelHidden ? null : (
        <span id={labelId} className={clsx(styles.label, locked && styles.labelLocked)}>
          {label}
        </span>
      )}
    </span>
  );
}
